import { Flex, Box, useDisclosure, Icon, IconButton, DrawerContent } from '@chakra-ui/react';
import React, { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { IconPrinter } from 'assets/icons';
import Button from 'components/Button';
import Drawer, { ControllableDrawer } from 'components/Drawer';
import Tabs, { TabHeader, TabPanels } from 'components/Tabs';
import ImportSchedule from 'containers/Scheduling/RightDrawer/ImportSchedule';
import ScheduleForm from 'containers/Scheduling/RightDrawer/ScheduleForm';
import SubscribeCalendar from 'containers/Scheduling/RightDrawer/SubscribeCalendar';
import { roles } from 'global/rolesAndPermissions';
import { useUserObject } from 'store/useUserStore';

import PendingShift from './PendingShift';
import Schedule from './Schedule';

const SchedulingTabs: React.FC<unknown> = () => {
  const { t } = useTranslation(['scheduling', 'common']);
  const userStore = useUserObject();

  const tabPanels = useMemo(() => [{ panel: <Schedule /> }, { panel: <PendingShift /> }], []);
  const tabHeaders = useMemo(() => [{ label: t('common:schedule') }, { label: t('common:pendingShift') }], [t]);
  const [activeTabIndex, setActiveTabIndex] = useState<number>(0);

  const {
    isOpen: isScheduleFormDrawerOpen,
    onOpen: onScheduleFormDrawerOpen,
    onClose: onScheduleFormDrawerClose,
  } = useDisclosure();
  const {
    isOpen: isImportScheduleDrawerOpen,
    onOpen: onImportScheduleDrawerOpen,
    onClose: onImportScheduleDrawerClose,
  } = useDisclosure();

  const handleTabChange = useCallback((index: number) => {
    setActiveTabIndex(index);
  }, []);

  const handlePrintButtonclick = (e: any) => {
    e.preventDefault();
    window.print();
  };

  // Display only for user having employee role
  if (userStore?.userInfo?.roleId === roles.ADMIN || userStore?.companyInfo?.enableScheduleAccept === 0)
    return (
      <Box>
        <ControllableDrawer isOpen={isScheduleFormDrawerOpen} onClose={onScheduleFormDrawerClose}>
          <ScheduleForm onClose={onScheduleFormDrawerClose} />
        </ControllableDrawer>
        <ControllableDrawer isOpen={isImportScheduleDrawerOpen} onClose={onImportScheduleDrawerClose}>
          <DrawerContent>
            <ImportSchedule onClose={onScheduleFormDrawerClose} />
          </DrawerContent>
        </ControllableDrawer>
        <Flex
          justifyContent="flex-end"
          mb="3"
          className="hidden-print"
          mt={{ base: '5', md: '0' }}
          sx={{ gap: '8px' }}
          flexWrap="wrap"
        >
          <IconButton
            aria-label="Icon printer"
            icon={<Icon as={IconPrinter} sx={{ '& path': { fill: 'white' } }} />}
            onClick={handlePrintButtonclick}
          />
          <Drawer
            drawerTrigger={onOpen => (
              <Button
                bg="secondary.50"
                color="secondary.400"
                _hover={{
                  bg: 'secondary.100',
                }}
                _active={{
                  bg: 'secondary.100',
                }}
                onClick={onOpen}
              >
                {t('common:subscribe')}
              </Button>
            )}
          >
            {onClose => <SubscribeCalendar onClose={onClose} />}
          </Drawer>
          {userStore?.userInfo?.roleId === roles.ADMIN || userStore?.permissions?.manageSchedules ? (
            <>
              <Button colorScheme="secondary" onClick={onImportScheduleDrawerOpen}>
                {t('scheduling:importSchedule')}
              </Button>
              <Button colorScheme="secondary" onClick={onScheduleFormDrawerOpen}>
                {t('scheduling:addSchedule')}
              </Button>
            </>
          ) : null}
        </Flex>
        <Schedule />
      </Box>
    );

  return (
    <>
      <ControllableDrawer isOpen={isScheduleFormDrawerOpen} onClose={onScheduleFormDrawerClose}>
        <ScheduleForm onClose={onScheduleFormDrawerClose} />
      </ControllableDrawer>
      <ControllableDrawer isOpen={isImportScheduleDrawerOpen} onClose={onImportScheduleDrawerClose}>
        <DrawerContent>
          <ImportSchedule onClose={onScheduleFormDrawerClose} />
        </DrawerContent>
      </ControllableDrawer>
      <Tabs onChange={handleTabChange} className="hidden-print">
        <Flex justifyContent="space-between" flexDirection={{ base: 'column', md: 'row' }}>
          <TabHeader tabHeaders={tabHeaders} />
          {activeTabIndex === 0 ? (
            <Flex justifyContent="flex-end" mb="3" mt={{ base: '5', md: '0' }}>
              <IconButton
                aria-label="Icon printer"
                icon={<Icon as={IconPrinter} sx={{ '& path': { fill: 'white' } }} />}
                mr="3"
              />
              <Drawer
                drawerTrigger={onOpen => (
                  <Button
                    bg="secondary.50"
                    color="secondary.400"
                    _hover={{
                      bg: 'secondary.100',
                    }}
                    _active={{
                      bg: 'secondary.100',
                    }}
                    mr="3"
                    onClick={onOpen}
                  >
                    {t('common:subscribe')}
                  </Button>
                )}
              >
                {onClose => <SubscribeCalendar onClose={onClose} />}
              </Drawer>
              {userStore?.userInfo?.roleId === roles.ADMIN || userStore?.permissions?.manageSchedules ? (
                <>
                  <Button colorScheme="secondary" onClick={onImportScheduleDrawerOpen} mr="2">
                    {t('scheduling:importSchedule')}
                  </Button>
                  <Button colorScheme="secondary" onClick={onScheduleFormDrawerOpen}>
                    {t('scheduling:addSchedule')}
                  </Button>
                </>
              ) : null}
            </Flex>
          ) : null}
        </Flex>
        <TabPanels tabPanels={tabPanels} />
      </Tabs>
    </>
  );
};

export default SchedulingTabs;
